// 後端通訊內容日誌 — 印出「解密後」的明文封包：cmd、解碼內容、hex。
// DEV 預設開；production build 需設 VITE_LOG_NET=true 才會輸出（以 console.info 直接印，
// 不受 logger 等級限制，故 build 版也看得到）。以 VITE_LOG_NET=false 可強制關閉。
// 攔截點在 GameServerClient（送出 PacketCmd 前的明文 payload、收到並解密後的 ResponseInfo）。
import type { ResponsePacket } from './PacketCodec'
import { lookupType } from './proto/protoRoot'

const ENABLED =
  import.meta.env['VITE_LOG_NET'] !== 'false' &&
  (import.meta.env.DEV || import.meta.env['VITE_LOG_NET'] === 'true')

// 專用輸出通道（不經 logger 等級，確保 build 版啟用時也會印）。
function out(...args: unknown[]): void {
  console.info('[FaFaCat][NET]', ...args)
}

// hex 輸出上限（bytes），避免大封包洗版。
const HEX_MAX = 512

// 送出 payload 的 proto 型別（GetAESKey 為原始 RSA 公鑰，非 proto）。
const SEND_TYPE: Readonly<Record<string, string>> = {
  UpdateDeviceInfo: 'Gateway.DeviceInfo',
  Login: 'Gateway.LoginCmd',
  GetGameInfo: 'Gateway.GetGameInfoCmd',
  GetBalance: 'Gateway.GetBalanceCmd',
  Slot001: 'Gateway.ClientSpinCmd',
}

// 回應 Data 的 proto 型別（Slot001 為 Any→SlotResult，特殊處理；其餘可能無 Data）。
const RECV_TYPE: Readonly<Record<string, string>> = {
  GetGameInfo: 'Gateway.RespGetGameInfo',
  GetBalance: 'Gateway.RespGetBalance',
}

const DECODE_OPTS = { longs: String, bytes: String, enums: String, defaults: true, arrays: true }

function toHex(bytes: Uint8Array): string {
  const slice = bytes.subarray(0, HEX_MAX)
  const s = Array.from(slice, (b) => b.toString(16).padStart(2, '0')).join(' ')
  return bytes.length > HEX_MAX ? `${s} …(+${bytes.length - HEX_MAX}B)` : s
}

function decode(typeName: string, bytes: Uint8Array): unknown {
  const type = lookupType(typeName)
  return type.toObject(type.decode(bytes), DECODE_OPTS)
}

/** 印出送出後端的明文封包（payload 為加密前的內層 bytes）。 */
export function logSend(cmd: string, requestId: string, payload: Uint8Array): void {
  if (!ENABLED) return
  out(`📤 送出 cmd=${cmd} req=${requestId} len=${payload.length}`)
  const typeName = SEND_TYPE[cmd]
  if (typeName !== undefined) {
    try {
      out('   內容:', decode(typeName, payload))
    } catch (error) {
      out('   內容解碼失敗:', error)
    }
  } else if (cmd === 'GetAESKey') {
    out('   內容: RSA 公鑰 (SPKI DER)')
  }
  out(`   hex: ${toHex(payload)}`)
}

/** 印出收到並解密後的 ResponseInfo（欄位 + Data 解碼 + hex）。 */
export function logRecv(res: ResponsePacket): void {
  if (!ENABLED) return
  const head =
    `📥 收到 cmd=${res.cmd} code=${res.code}` +
    (res.message ? ` msg="${res.message}"` : '') +
    ` req=${res.requestId}` +
    (res.balance !== 0n ? ` balance=${res.balance}` : '') +
    (res.currency ? ` currency=${res.currency}` : '') +
    (res.txnId ? ` txnId=${res.txnId}` : '') +
    (res.totalWin !== 0n ? ` totalWin=${res.totalWin}` : '') +
    ` dataLen=${res.data.length}`
  out(head)

  if (res.data.length === 0) return
  const typeName = RECV_TYPE[res.cmd]
  try {
    if (typeName !== undefined) {
      out('   Data:', decode(typeName, res.data))
    } else if (res.cmd === 'Slot001') {
      // spin 回應 Data = google.protobuf.Any → Slot001FaFaCat.SlotResult
      const any = lookupType('google.protobuf.Any').decode(res.data) as unknown as {
        value?: Uint8Array
      }
      out('   Data(SlotResult):', decode('Slot001FaFaCat.SlotResult', any.value ?? new Uint8Array(0)))
    }
  } catch (error) {
    out('   Data 解碼失敗:', error)
  }
  out(`   hex: ${toHex(res.data)}`)
}
